/**
 * Branded Export — shared constants, feature flag, and the template registry.
 *
 * This feature is purely additive: it composites a single captured floor-plan
 * raster (and a QR code) onto one of the pre-designed template artboards shipped
 * with the app. Existing download paths (`downloadLayout` in UploadFloor,
 * `downloadGalleryImage` in LayoutVersionSlider) are never modified — if this
 * feature is flagged off, or if any step in the new path fails, callers fall
 * back to those.
 *
 * Kill switch: build-time env var, defaulting ON, matching the existing pattern
 * used by REACT_APP_ENABLE_SPATIAL_VISUAL_SYSTEM / REACT_APP_TRIPWIRE_BLOCK_SAVE.
 * Set REACT_APP_ENABLE_BRANDED_EXPORT=false to revert to the legacy download.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ADDING / EDITING TEMPLATES  (see src/Assets/brandedExport/README.md)
 * ─────────────────────────────────────────────────────────────────────────────
 * Templates are auto-discovered from `src/Assets/brandedExport/` by filename —
 * no import or code change is needed. The filename IS the switch state:
 *
 *   qr.svg                     QR, no logo, no disclaimer   (default saved)
 *   qr-logo.svg                QR + partner logo
 *   qr-disclaimer.svg          QR + disclaimer
 *   qr-logo-disclaimer.svg     QR + partner logo + disclaimer
 *   bare.svg                   no QR, no disclaimer          (layout not saved)
 *   bare-disclaimer.svg        no QR + disclaimer
 *
 * To edit a template: replace the file (keep the name). To add a variant: drop a
 * file whose name is `<qr|bare>[-logo][-disclaimer]` (svg or png). SVG keeps the
 * export crisp at any scale and is preferred. Every artboard must share the same
 * canvas + box geometry below so toggling never shifts the plan or QR.
 */

export const BRANDED_EXPORT_ENABLED =
  process.env.REACT_APP_ENABLE_BRANDED_EXPORT !== "false";

/**
 * Fixed template canvas — matches the provided artboards (US-Letter portrait).
 * The floor plan and QR are dropped INTO fixed boxes via object-fit: contain, so
 * all branding is pixel-identical on every export regardless of the plan's
 * native resolution or aspect ratio. Every variant shares this exact canvas and
 * box geometry, so toggling never shifts anything.
 */
export const TEMPLATE = Object.freeze({ WIDTH: 612, HEIGHT: 792 });

/** Floor-plan drop zone within the canvas (measured from the artboards). */
export const FLOOR_BOX = Object.freeze({ x: 30, y: 70, width: 551, height: 547 });

/** QR drop zone within the canvas (measured from the artboards). */
export const QR_BOX = Object.freeze({ x: 30, y: 636, size: 76 });

/** Export scale on top of the design units for a crisp raster (612*3 = 1836). */
export const EXPORT_SCALE = 3;

/** Hard ceiling shared with the legacy pipeline to avoid mobile OOM. */
export const MAX_EXPORT_DIMENSION = 4096;

/** Supported output formats for the download. */
export const EXPORT_FORMATS = Object.freeze({
  JPEG: "jpeg",
  PNG: "png",
});

export const DEFAULT_EXPORT_FORMAT = EXPORT_FORMATS.JPEG;

/**
 * Auto-discovered template registry: { "<name>": <assetUrl> }.
 * `require.context` is a webpack (CRA) build-time feature, so dropping a file in
 * the folder is enough — it is bundled and keyed by its filename automatically.
 */
const templateContext = require.context(
  "../Assets/brandedExport",
  false,
  /\.(svg|png)$/
);

export const TEMPLATES = Object.freeze(
  templateContext.keys().reduce((acc, key) => {
    const name = key.replace(/^\.\//, "").replace(/\.(svg|png)$/i, "");
    const mod = templateContext(key);
    acc[name] = mod && mod.default ? mod.default : mod;
    return acc;
  }, {})
);

/** Every artboard URL, so callers can pre-resolve them all to data URLs. */
export const ALL_TEMPLATE_SVGS = Object.freeze(Object.values(TEMPLATES));

/**
 * Build the template name for the current options from the naming convention.
 *  - No QR (layout not saved yet) → `bare` variants, so a dead/missing QR is
 *    never framed. The partner logo only exists on QR artboards.
 *  - Otherwise choose by the partner-logo + disclaimer toggles.
 */
export function templateNameFor({ hasQr, showLogo, showDisclaimer }) {
  if (!hasQr) return `bare${showDisclaimer ? "-disclaimer" : ""}`;
  return `qr${showLogo ? "-logo" : ""}${showDisclaimer ? "-disclaimer" : ""}`;
}

/**
 * Resolve the template artwork URL for the current options, with graceful
 * fallbacks so a missing variant never breaks the export.
 */
export function selectTemplateSvg(options) {
  const name = templateNameFor(options);
  return (
    TEMPLATES[name] ||
    TEMPLATES[options.hasQr ? "qr" : "bare"] ||
    ALL_TEMPLATE_SVGS[0] ||
    null
  );
}
